"use client";

import React from "react";
import { useIsMounted } from "@/lib/useIsMounted";

interface HeroRotatingPhrasesProps {
  phrases: string[];
  className?: string;
  showUnderline?: boolean;
  align?: "left" | "center" | "responsive";
}

export default function HeroRotatingPhrases({
  phrases,
  className = "",
  showUnderline = true,
  align = "responsive",
}: HeroRotatingPhrasesProps) {
  const mounted = useIsMounted();

  const alignClass =
    align === "center"
      ? "hero-rotating-phrases-center"
      : align === "left"
      ? "hero-rotating-phrases-left"
      : "hero-rotating-phrases-responsive";

  const renderUnderline = () =>
    showUnderline && (
      <svg
        aria-hidden="true"
        viewBox="0 0 300 12"
        preserveAspectRatio="none"
        className="hero-phrase-underline"
      >
        <path
          d="M2 8.5c48-4.5 96-6 148-5.2 50 .8 98 2.9 148 6.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
    );

  // During SSR and before hydration: render ONLY the primary canonical phrase.
  // This guarantees search crawlers (Googlebot) and screen readers index a clean,
  // single, properly spaced sentence without duplicated phrases or missing spaces.
  if (!mounted) {
    return (
      <span className={`hero-rotating-phrases ${alignClass} ${className}`}>
        <span className="hero-rotating-phrase">
          <span className="hero-phrase-inner">
            <span className="hero-phrase-text">{phrases[0]}</span>
            {renderUnderline()}
          </span>
        </span>
      </span>
    );
  }

  // After hydration: activate pure CSS multi-phrase rotation for visual visitors,
  // keeping a single canonical phrase for screen readers and JS crawlers.
  return (
    <>
      <span className="sr-only">{" " + phrases[0]}</span>
      <span className={`hero-rotating-phrases ${alignClass} ${className}`} aria-hidden="true">
        {phrases.map((phrase, i) => (
          <span key={i} className="hero-rotating-phrase">
            <span className="hero-phrase-inner">
              <span className="hero-phrase-text">{phrase}</span>
              {renderUnderline()}
            </span>
          </span>
        ))}
      </span>
    </>
  );
}
